import { useEffect, useRef, useState } from 'react';
import { useNotifs } from '../contexts/index';
import type { IntegrityDisposition } from '../core/rules/integritySignals';
import { shouldPersistIntegritySuppression } from '../core/rules/integritySuppression';
import { usePresentAppError } from './usePresentAppError';
import { useSupportLearning } from './useSupportLearning';

const SAVED_FLASH_MS = 2_400;

type BusyState = {
  signalKey: string;
  disposition: IntegrityDisposition;
} | null;

function signalKey(runId: string, code: string, houseId?: string) {
  return `${runId}::${code}::${houseId ?? ''}`;
}

/** Optimistic disposition updates for the Integrity screen. */
export function useIntegrityDisposition(t: Record<string, string>) {
  const { push, dismissMatchingIntegrity } = useNotifs();
  const presentAppError = usePresentAppError();
  const { setDisposition } = useSupportLearning();
  const [busy, setBusy] = useState<BusyState>(null);
  const [optimistic, setOptimistic] = useState<Record<string, IntegrityDisposition>>({});
  const [savedKey, setSavedKey] = useState('');
  const inFlightRef = useRef(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const onDisposition = async (
    runId: string,
    signalCode: string,
    houseId: string | undefined,
    disposition: IntegrityDisposition,
    previous: IntegrityDisposition,
  ) => {
    if (inFlightRef.current) return;
    const key = signalKey(runId, signalCode, houseId);
    inFlightRef.current = true;
    setBusy({ signalKey: key, disposition });
    setOptimistic((prev) => ({ ...prev, [key]: disposition }));
    try {
      await setDisposition({ runId, signalCode, houseId, disposition });
      if (shouldPersistIntegritySuppression(disposition)) {
        dismissMatchingIntegrity({ runId, signalCode, houseId });
      }
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      setSavedKey(key);
      savedTimerRef.current = setTimeout(() => {
        setSavedKey((current) => (current === key ? '' : current));
        savedTimerRef.current = null;
      }, SAVED_FLASH_MS);
    } catch (err) {
      setOptimistic((prev) => {
        const next = { ...prev };
        if (previous === 'open') delete next[key];
        else next[key] = previous;
        return next;
      });
      presentAppError(err, { t, push, forceToast: true });
    } finally {
      inFlightRef.current = false;
      setBusy(null);
    }
  };

  return { busy, optimistic, savedKey, saving: busy != null, onDisposition };
}
